import React,{useEffect,useState} from 'react';
import {motion} from 'framer-motion';
import {MapContainer,TileLayer,CircleMarker,Popup,Polyline,useMap} from 'react-leaflet';
import L from 'leaflet';
import {philippineFaultLines,getFaultLineStyle,createFaultPopupContent} from '../data/faultLines';
import {philippineVolcanoes,getVolcanoMarkerStyle,createVolcanoPopupContent} from '../data/volcanoes';
const getMarkerConfig=(calamity)=> {
let color,size,icon;
if (calamity.type==='earthquake') {
if (calamity.earthquake_type==='aftershock') {color='#9ca3af';size=6;icon='📉';}
else if (calamity.earthquake_type==='main_shock') {
if (calamity.magnitude >=7) {color='#dc2626';size=22;}
else if (calamity.magnitude >=6) {color='#ea580c';size=18;}
else {color='#d97706';size=14;}
icon='💥';
}
else {
if (calamity.magnitude >=6) {color='#ea580c';size=16;}
else if (calamity.magnitude >=5) {color='#d97706';size=12;}
else {color='#6b7280';size=8;}
icon='🌍';
}}
else if (calamity.type==='volcanic') {
if (calamity.magnitude >=4) {color='#dc2626';size=20;}
else if (calamity.magnitude >=2) {color='#ea580c';size=16;}
else {color='#f59e0b';size=12;}
icon='🌋';
}
else {
color={'flood': '#3b82f6','tidal': '#06b6d4','weather': '#8b5cf6','tsunami': '#ef4444'}[calamity.type] || '#9ca3af';
size={'flood': 15,'tidal': 12,'weather': 18,'tsunami': 20}[calamity.type] || 10;
icon={'flood': '🌊','tidal': calamity.event_type==='high_tide' ? '⬆️' : '⬇️','weather': '🌀','tsunami': '🌋'}[calamity.type] || '⚠️';
}
return {color,size,icon};
};
const createPopupContent=(calamity,markerConfig,aftershocks=[])=> {
const time=new Date(calamity.time_utc).toLocaleString('en-PH',{timeZone: 'Asia/Manila',dateStyle: 'medium',timeStyle: 'short'});
let specificInfo='';
if (calamity.type==='earthquake') {
specificInfo=`
<div><strong>Depth:</strong> ${calamity.depth_km.toFixed(1)} km</div>
<div><strong>Type:</strong> ${calamity.earthquake_type?.replace('_',' ') || 'Independent'}</div>
${calamity.tsunami?.has_warning ? '<div class="text-red-600 font-medium">⚠️ Tsunami Risk</div>' : ''}
`;
}
else if (calamity.type==='volcanic') {
specificInfo=`
<div><strong>Volcano:</strong> ${calamity.volcano_info?.volcano_name || 'Unknown'}</div>
<div><strong>Activity:</strong> ${calamity.event_type?.replace('_',' ') || 'Unknown'}</div>
<div><strong>Alert Level:</strong> ${calamity.volcano_info?.alert_level || 'N/A'}</div>
`;
}
let aftershockInfo='';
if (aftershocks.length > 0) {
aftershockInfo=`
<div class="mt-3 pt-3 border-t">
<div class="font-medium text-sm mb-2">📉 Aftershocks (${aftershocks.length}):</div>
<div class="space-y-1 text-xs">
${aftershocks.slice(0,5).map(as=> `
<div class="flex justify-between">
<span>M${as.magnitude.toFixed(1)}</span>
<span class="text-gray-500">${new Date(as.time_utc).toLocaleTimeString('en-PH',{hour: '2-digit',minute: '2-digit'})}</span>
</div>
`).join('')}
${aftershocks.length > 5 ? `<div class="text-gray-500 italic">+ ${aftershocks.length - 5} more</div>` : ''}
</div>
</div>
`;
}
const detailButton=calamity.detail_url ? `<a href="${calamity.detail_url}" target="_blank" rel="noopener noreferrer" class="mt-3 block w-full text-center px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600">View Details</a>` : '';
return `
<div class="p-1 min-w-[250px]">
<div class="font-semibold text-lg mb-2 flex items-center space-x-2">
<span>${markerConfig.icon}</span>
<span style="color: ${markerConfig.color}">${calamity.type.charAt(0).toUpperCase() + calamity.type.slice(1)}${calamity.magnitude ? ` - L${calamity.magnitude}` : ''}</span>
</div>
<div class="space-y-2 text-sm">
<div><strong>Time:</strong> ${time}</div>
<div><strong>Location:</strong> ${calamity.place_text}</div>
${calamity.nearest_city ? `<div><strong>Nearest City:</strong> ${calamity.nearest_city.name} (${calamity.nearest_city.distance_km} km)</div>` : ''}
<div><strong>Province:</strong> ${calamity.province}</div>
${specificInfo}
${calamity.alert !=='None' ? `<div><strong>Alert:</strong> <span class="font-medium" style="color: ${markerConfig.color}">${calamity.alert}</span></div>` : ''}
</div>
${aftershockInfo}
${detailButton}
</div>
`;
};
const MapEvents=({calamities,onCalamityClick,filters})=> {
const map=useMap();
useEffect(()=> {
if (!calamities || calamities.length===0) return;
const bounds=L.latLngBounds(calamities.map(c=> [c.lat,c.lng]));
if (bounds.isValid()) {
map.fitBounds(bounds,{padding: [50,50]});
}},[calamities,map,filters]);
const filteredCalamities=calamities.filter(c=> filters?.types?.includes(c.type) ?? true );
const calamityMap=new Map(filteredCalamities.map(c=> [c.id,c]));
const mainShocks=new Map();
const independentEvents=[];
for (const calamity of filteredCalamities) {
if (calamity.earthquake_type==='main_shock') {
mainShocks.set(calamity.id,{mainShock: calamity,aftershocks: []});
}}
for (const calamity of filteredCalamities) {
if (calamity.earthquake_type==='aftershock' && calamity.aftershock_of) {
const parent=mainShocks.get(calamity.aftershock_of);
if (parent) {
parent.aftershocks.push(calamity);}
else {independentEvents.push(calamity);}}
else if (calamity.earthquake_type !=='main_shock') {
independentEvents.push(calamity);
}}
return (
<>
{philippineFaultLines.map(fault=> (
<Polyline key={fault.id} positions={fault.coordinates} pathOptions={getFaultLineStyle(fault)} >
<Popup>
<div dangerouslySetInnerHTML={{__html: createFaultPopupContent(fault)}} />
</Popup>
</Polyline>
))}
{philippineVolcanoes.map(volcano=> (
<CircleMarker key={`volcano-${volcano.id}`} center={[volcano.lat,volcano.lng]} pathOptions={getVolcanoMarkerStyle(volcano)} >
<Popup>
<div dangerouslySetInnerHTML={{__html: createVolcanoPopupContent(volcano)}} />
</Popup>
</CircleMarker>
))}
{Array.from(mainShocks.values()).map(({mainShock,aftershocks})=> {
const mainMarkerConfig=getMarkerConfig(mainShock);
return (
<React.Fragment key={mainShock.id}>
<CircleMarker center={[mainShock.lat,mainShock.lng]} pathOptions={{color: '#ffffff',fillColor: mainMarkerConfig.color,fillOpacity: 0.8,weight: 3}} radius={mainMarkerConfig.size} eventHandlers={{click: ()=> onCalamityClick(mainShock)}} >
<Popup>
<div dangerouslySetInnerHTML={{__html: createPopupContent(mainShock,mainMarkerConfig,aftershocks)}} />
</Popup>
</CircleMarker>
{aftershocks.map(aftershock=> {
const aftershockConfig=getMarkerConfig(aftershock);
return (
<React.Fragment key={aftershock.id}>
<CircleMarker center={[aftershock.lat,aftershock.lng]} pathOptions={{color: '#ffffff',fillColor: aftershockConfig.color,fillOpacity: 0.6,weight: 2}} radius={aftershockConfig.size} eventHandlers={{click: ()=> onCalamityClick(aftershock)}} >
<Popup>
<div dangerouslySetInnerHTML={{__html: createPopupContent(aftershock,aftershockConfig)}} />
</Popup>
</CircleMarker>
<Polyline positions={[[mainShock.lat,mainShock.lng],[aftershock.lat,aftershock.lng]]} pathOptions={{color: '#9ca3af',weight: 1,opacity: 0.5,dashArray: '5,5'}} />
</React.Fragment>
);
})}
</React.Fragment>
);
})}
{independentEvents.map(calamity=> {
const markerConfig=getMarkerConfig(calamity);
return (
<CircleMarker key={calamity.id} center={[calamity.lat,calamity.lng]} pathOptions={{color: '#ffffff',fillColor: markerConfig.color,fillOpacity: 0.7,weight: 2}} radius={markerConfig.size} eventHandlers={{click: ()=> onCalamityClick(calamity)}} >
<Popup>
<div dangerouslySetInnerHTML={{__html: createPopupContent(calamity,markerConfig)}} />
</Popup>
</CircleMarker>
);
})}
</>
);
};
const CalamityMap=({calamities,onCalamityClick,filters})=> {
const getLiveDescription=()=> {
if (!calamities || calamities.length===0) return "No active hazards detected in the Philippines.";
const activeCount=calamities.filter(c=> c.alert !=='None').length;
const warningCount=calamities.filter(c=> c.alert==='Warning').length;
const watchCount=calamities.filter(c=> c.alert==='Watch').length;
const earthquakeCount=calamities.filter(c=> c.type==='earthquake').length;
const volcanicCount=calamities.filter(c=> c.type==='volcanic').length;
const floodCount=calamities.filter(c=> c.type==='flood').length;
let description=`Currently monitoring ${calamities.length} events`;
if (activeCount > 0) description+=` with ${activeCount} active alerts`;
if (warningCount > 0) description+=` (${warningCount} warnings${watchCount > 0 ? `, ${watchCount} watches` : ''})`;
description+=". ";
if (earthquakeCount > 0) description+=`${earthquakeCount} seismic event${earthquakeCount > 1 ? 's' : ''} detected`;
if (volcanicCount > 0) description+=`, ${volcanicCount} volcanic activit${volcanicCount > 1 ? 'ies' : 'y'}`;
if (floodCount > 0) description+=`, ${floodCount} flood${floodCount > 1 ? 's' : ''}`;
description+=" across the Philippines. Click markers for detailed information.";
return description;
};
return (
<motion.div initial={{opacity: 0}} animate={{opacity: 1}} className="bg-white rounded-lg shadow-md overflow-hidden">
<div className="p-4 border-b">
<h2 className="text-lg font-semibold text-gray-900">Live Calamity Map</h2>
<p className="text-sm text-gray-600">{calamities.length} events shown ({calamities.filter(c=> c.earthquake_type==='aftershock').length} aftershocks)</p>
<div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
<p className="text-sm text-blue-800 font-medium">📡 Live Status:</p>
<p className="text-xs text-blue-700 mt-1">{getLiveDescription()}</p>
</div>
</div>
<div className="relative">
<MapContainer center={[12.8797,121.7740]} zoom={6} scrollWheelZoom={true} style={{height: '500px',width: '100%',zIndex: 0}} >
<TileLayer attribution='&copy;<a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
<MapEvents calamities={calamities} onCalamityClick={onCalamityClick} filters={filters} />
</MapContainer>
<div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-lg text-xs w-48 z-[1000]">
<div className="font-semibold mb-2">Events</div>
<div className="space-y-1 mb-2">
<div className="flex items-center">
<div className="w-3 h-3 mr-2 rounded-full bg-red-600"></div>
<span>💥 EQ M7+</span>
</div>
<div className="flex items-center">
<div className="w-3 h-3 mr-2 rounded-full bg-orange-600"></div>
<span>💥 EQ M6+</span>
</div>
<div className="flex items-center">
<div className="w-3 h-3 mr-2 rounded-full bg-amber-600"></div>
<span>💥 EQ M5+</span>
</div>
<div className="flex items-center">
<div className="w-3 h-3 mr-2 rounded-full bg-gray-400"></div>
<span>📉 Aftershock</span>
</div>
<div className="flex items-center">
<div className="w-3 h-3 mr-2 rounded-full bg-orange-500"></div>
<span>🌋 Volcanic</span>
</div>
<div className="flex items-center">
<div className="w-3 h-3 mr-2 rounded-full bg-blue-500"></div>
<span>🌊 Flood</span>
</div>
<div className="flex items-center">
<div className="w-3 h-3 mr-2 rounded-full bg-cyan-500"></div>
<span>⬆️⬇️ Tide</span>
</div>
<div className="flex items-center">
<div className="w-3 h-3 mr-2 rounded-full bg-purple-500"></div>
<span>🌀 Weather</span>
</div>
</div>
<div className="border-t pt-2">
<div className="font-semibold mb-2">Fault Lines</div>
<div className="space-y-1">
<div className="flex items-center">
<div className="w-6 h-0.5 mr-2 bg-red-600"></div>
<span>Major</span>
</div>
<div className="flex items-center">
<div className="w-6 h-0.5 mr-2 bg-orange-600"></div>
<span>Strike-Slip</span>
</div>
<div className="flex items-center">
<div className="w-6 h-0.5 mr-2 bg-blue-600 border-dashed border-b-2" style={{borderStyle: 'dashed'}}></div>
<span>Subduction</span>
</div>
</div>
</div>
</div>
</div>
</motion.div>
);
};
export default CalamityMap;